import { Component, Input, OnInit, ViewChild } from '@angular/core';
import { NG_VALUE_ACCESSOR } from '@angular/forms';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { ComboBoxComponent } from '@progress/kendo-angular-dropdowns';
import { Subject, debounceTime, switchMap, map, of, merge } from 'rxjs';
import { IAssignment, ISearchAssignmentsRequest, TaskStatues } from 'src/app/assignment/models/assignment';
import { AssignmentService } from 'src/app/assignment/services/assignment.service';
import { IBaseSearchRequest } from 'src/app/core/models/base';

@UntilDestroy()
@Component({
    selector: 'app-assignment-combobox',
    templateUrl: './assignment-combobox.component.html',
    styleUrls: ['./assignment-combobox.component.css'],
    providers: [
        {
            provide: NG_VALUE_ACCESSOR,
            useExisting: AssignmentComboboxComponent,
            multi: true,
        },
    ],
    standalone: false
})
export class AssignmentComboboxComponent implements OnInit {

  constructor(private assignmentService : AssignmentService) {
    this.initData();
   }

  ngOnInit(): void {
  }

  @ViewChild(ComboBoxComponent, { static: true })
  kendoComboBox: ComboBoxComponent;

  @Input() disabled = false;
  @Input() readonly = false;
  @Input() valuePrimitive = true;
  @Input() set initialAssignment(value: IAssignment) {
    this.initialSub$.next(value);
    this._initialAssignment = value;
    this._value = value;
  }
  @Input() hideLink = false;
  @Input() hideClosedAssigments = false;

  private searchSub$ = new Subject<IBaseSearchRequest>();
  private initialSub$ = new Subject<string | IAssignment>();
  public assignments: (IAssignment & { name: string })[] = [];
  private _initialAssignment: IAssignment;

  public _value: any;

  get value(): any {
    let copy = this._value;

    if (!this.valuePrimitive) {
      copy = { ...copy };
      delete copy.name;
    }

    return copy;
  }

  onChange: any = (value: any) => {};
  onTouch: any = () => {};

  writeValue(value: any): void {
    if (value === null) {
      this.kendoComboBox.reset();
    }

    this._value = value;

    if (this._initialAssignment) {
      return;
    }

    this.initialSub$.next(value);
  }

  registerOnChange(fn: (value: any) => void): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: (value: any) => void): void {
    this.onTouch = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    this.disabled = isDisabled;
  }

  initData() {
    const searchAssignment$ = this.searchSub$.pipe(
      debounceTime(300),
      switchMap((req) =>
        this.assignmentService.searchAssignments(req).pipe(
          map((response) => {
            return response.data;
          })
        )
      )
    );

    const initialAssignment$ = this.initialSub$.pipe(
      switchMap((value) => {
        if (!value) {
          return of([]);
        }

        if (typeof value === "string") {
          return this.assignmentService.getAssignment(value).pipe(
            map((response) => {
              return [response];
            })
          );
        } else {
          return of([value]);
        }
      })
    );

    merge(initialAssignment$, searchAssignment$)
      .pipe(untilDestroyed(this))
      .subscribe((data) => {
        this.assignments = data.map((assignment) => {
          return {
            ...assignment,
            name: assignment.title + " | " + assignment.internalNumber,
          };
        });;
      });
  }

  onFilterChange(value) {
    if (value) {
      const request : ISearchAssignmentsRequest = { keyword: value, pageNumber: 0, pageSize: 999999 };

      if(this.hideClosedAssigments){
        request.taskStatuses = [TaskStatues.open, TaskStatues.inProgress]
      }

      this.searchSub$.next(request);
    }
  }

  onValueChange(value) {
    if (value && !this.valuePrimitive) {
      value = { ...value };
      delete value.name;
    }

    if (!this.disabled) {
      this.onChange(value);
      this.onTouch(value);
      this._value = value;
    }
  }

  reset() {
    this.writeValue(null);
  }


}
